import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import client from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';

export default function AccountPage() {
  const { user, loading } = useAuth();
  const { toast } = useToast();
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [busy, setBusy] = useState(false);

  if (loading) return <div className="p-10 text-slate-400">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 6) {
      toast({ title: 'Password too short', description: 'Use at least 6 characters.' });
      return;
    }
    if (form.new_password !== form.confirm) {
      toast({ title: "Passwords don't match" });
      return;
    }
    setBusy(true);
    try {
      await client.post('/auth/change-password', {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast({ title: 'Password updated' });
      setForm({ current_password: '', new_password: '', confirm: '' });
    } catch (err) {
      toast({ title: 'Could not update password', description: err?.response?.data?.detail || '' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-16">
      <div className="text-xs font-mono uppercase tracking-widest text-cyan-300">Account</div>
      <h1 className="font-display text-3xl font-bold mt-1">{user.name}</h1>
      <p className="text-slate-400 mt-1 text-sm">{user.company_email}</p>

      <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] p-8">
        <div className="text-sm font-medium text-slate-200">Change password</div>
        <form onSubmit={submit} className="mt-4 space-y-4">
          <div>
            <Label className="text-slate-300">Current password</Label>
            <Input type="password" required value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} className="bg-black/30 border-white/10 mt-1" />
          </div>
          <div>
            <Label className="text-slate-300">New password</Label>
            <Input type="password" required value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} className="bg-black/30 border-white/10 mt-1" />
          </div>
          <div>
            <Label className="text-slate-300">Confirm new password</Label>
            <Input type="password" required value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className="bg-black/30 border-white/10 mt-1" />
          </div>
          <Button disabled={busy} className="w-full bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-medium">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Update password
          </Button>
        </form>
      </div>
    </div>
  );
}
